// FILE: src/config/paceMessages.js
//
// Canned coaching copy for Intelligent Pace Coaching interventions (Phase 3).
// Centralized so the wording can be reviewed/edited in ONE place without
// touching logic. The pure reducer in services/paceInterventions.js selects
// which line to show; this file only holds the strings.
//
// Tone rules (docs/pace-coaching-proposal.md §1): supportive, never
// judgmental; "behind" framed as strategy, not failure.

export const PACE_MESSAGES = {
  // Fired once when the learner crosses INTO a pace-status band (4-band model).
  // 'onTrack' has no message — steady state is silence.
  status: {
    ahead:
      "Nice work. You're ahead of schedule—maintain your rhythm without rushing.",
    behind:
      "You're falling behind schedule. If uncertain, make your best choice, flag the question, and keep moving.",
    significantlyBehind:
      "You're well behind pace. Focus on maintaining momentum and reserve detailed analysis for flagged questions.",
  },

  // Fired once when the learner climbs back up from a behind band by a
  // meaningful margin. Reinforces good behavior.
  recovery:
    "Great recovery! You've regained valuable time over the last section. Keep trusting your preparation.",

  // Position-aware checkpoint copy, fired at the cert's milestones. 'behind'
  // overrides the positional line when the learner is behind at the checkpoint.
  milestone: {
    first:
      "Nice work! You're maintaining a steady pace and building momentum.",
    midway:
      "Many candidates slow down during this portion of the exam. Stay confident and keep moving.",
    finalStretch:
      "You're in the final stretch. Trust your preparation and avoid second-guessing unless you have a clear reason.",
    behind:
      "You've slowed slightly during the last section — consider capping difficult questions at ~90 seconds.",
  },

  // Fired once per question when a single question exceeds the time-sink
  // threshold.
  timeSink:
    "You've spent considerable time on this question. Consider making your best choice, flagging it for review, and continuing forward.",

  // Advisory personalization tips (docs §6), keyed by derivePaceProfile tag.
  // Surfaced in the break panel and (later) the results summary.
  personalization: {
    slowButAccurate:
      "Your accuracy is strong. Trust your instincts and avoid unnecessary second-guessing.",
    fastButErrorProne:
      "You're moving quickly, but accuracy has dipped. Take an extra moment to find the key decision in each scenario.",
    highFlagCount:
      "Good strategy. Keep progressing and revisit your flagged questions during review.",
    frequentAnswerChanges:
      "Your first instinct is often right. Avoid changing answers without a clear reason.",
  },

  // Break coaching panel recommendation, by pace bucket (see paceRecommendationKey).
  break: {
    onTrack:
      "You're pacing well. Keep your rhythm—steady and confident—and save any spare time to review flagged questions.",
    behind:
      "You're a little behind. Keep moving steadily, trust your instincts, and use Flag for Review instead of chasing certainty.",
    wellBehind:
      "You're well behind pace. Prioritize momentum—make your best choice, flag tough questions, and keep advancing.",
  },
};
